import Swiper from 'swiper';
import { Pagination, Autoplay, EffectFade, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import ShuffleImages from './modules/shuffleImages';

gsap.registerPlugin(ScrollTrigger);

// common variables
const mql = window.matchMedia('(min-width: 768px)');
const mqlLg = window.matchMedia('(min-width: 1280px)');

// reload on resize
mql.onchange = () => {
  location.reload();
};

// loading animation
const loading = document.getElementById('loading');

if (loading) {
  document.addEventListener('DOMContentLoaded', () => {
    const images = document.querySelectorAll<HTMLDivElement>(
      '.js-loading-flash-image'
    );
    let currentIndex = 0;
    const imageCount = images.length;
    const switchInterval = 200;

    let showKvCopyInterval = 500;

    const SESSION_DURATION = 24 * 60 * 60 * 1000;
    const lastVisit = localStorage.getItem('lastVisit');

    if (!lastVisit || Date.now() - parseInt(lastVisit) > SESSION_DURATION) {
      loading.classList.remove('is-session');
      localStorage.setItem('lastVisit', Date.now().toString());

      showKvCopyInterval = 2000;
    }

    images[0].style.opacity = '1';
    images.forEach((image, index) => {
      if (index !== 0) {
        image.style.opacity = '0';
      }
    });

    const switchImages = () => {
      images[currentIndex].style.opacity = '0';
      currentIndex++;

      if (currentIndex < imageCount - 1) {
        images[currentIndex].style.opacity = '1';
        setTimeout(switchImages, switchInterval);
      } else if (currentIndex === imageCount - 1) {
        images[currentIndex].style.opacity = '1';
      }
    };

    setTimeout(switchImages, switchInterval);

    setTimeout(() => {
      loading.classList.add('is-loaded');
    }, switchInterval * imageCount);

    const kvItems = document.querySelectorAll('.js-kv-item');

    kvItems.forEach((kvItem) => {
      gsap.set(kvItem, { y: '30px', opacity: 0 });
    });

    setTimeout(() => {
      kvItems.forEach((kvItem) => {
        gsap.fromTo(
          kvItem,
          { y: '30px', opacity: 0 },
          { y: '0%', opacity: 1, duration: 0.8, ease: 'power3.inOut' }
        );
      });
    }, showKvCopyInterval);

    window.addEventListener('scroll', () => {
      if (window.scrollY > 10) {
        kvItems.forEach((kvItem) => {
          gsap.to(
            kvItem,
            { y: '-30px', opacity: 0, duration: 0.5, ease: 'power2'},
          );
        });
      } else {
        kvItems.forEach((kvItem) => {
          gsap.to(
            kvItem,
            { y: '0%', opacity: 1, duration: 0.5, ease: 'power2'},
          );
        });
      }
    });
  });
}

// top kv
const frontPage = document.getElementById('front-page');

if (frontPage) {
  const kv = document.getElementById('kv');
  const fixedBg = document.getElementById('fixed-bg');

  const project = document.getElementById('project');
  const bgColor = document.querySelector('.js-bg-color');
  const banner = document.getElementById('banner');

  ScrollTrigger.create({
    trigger: kv,
    start: '35% top',
    end: 'bottom bottom',
    // markers: true,
    onEnter: () => {
      if (fixedBg) {
        fixedBg.classList.add('is-grayscale');
      }
    },
    onLeaveBack: () => {
      if (fixedBg) {
        fixedBg.classList.remove('is-grayscale');
      }
    },
  });

  if (project) {
    const projectContent = project.querySelector(
      '.js-project-content'
    ) as HTMLElement;
    const projectContentInner = projectContent.querySelector(
      '.js-project-content-inner'
    ) as HTMLElement;

    if (projectContent) {
      ScrollTrigger.create({
        pin: projectContentInner,
        trigger: projectContent,
        start: 'top top',
        end: 'bottom bottom',
        // markers: true,
      });
    }

    const projectSection = project.querySelectorAll('.js-project-section');
    const projectSectionBox = project.querySelectorAll(
      '.js-project-section-box'
    );

    projectSection.forEach((section, index) => {
      if (index !== 0) {
        const projectText = section.querySelectorAll('.js-project-text');

        projectText.forEach((text) => {
          gsap.set(text, { y: '105%' });
        });
      }
    });

    projectSectionBox.forEach((section, index) => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom top',
        // markers: true,
        onEnter: () => {
          if (index !== 0) {
            const projectText =
              projectSection[index].querySelectorAll('.js-project-text');
            projectText.forEach((text) => {
              gsap.fromTo(
                text,
                { y: '105%' },
                { y: '0%', duration: 0.1, ease: 'none' }
              );
            });

            const projectLink =
              projectSection[index].querySelector('.js-project-link');
            if (projectLink) {
              projectLink.classList.add('is-active');
            }
          }
        },

        onEnterBack: () => {
          const projectText =
            projectSection[index].querySelectorAll('.js-project-text');
          projectText.forEach((text) => {
            gsap.fromTo(
              text,
              { y: '-105%' },
              { y: '0%', duration: 0.1, ease: 'none' }
            );
          });
          const projectLink =
            projectSection[index].querySelector('.js-project-link');
          if (projectLink) {
            projectLink.classList.remove('is-active');
          }

          if (index + 1 < projectSection.length) {
            const nextProjectText =
              projectSection[index + 1].querySelectorAll('.js-project-text');
            nextProjectText.forEach((text) => {
              gsap.fromTo(
                text,
                { y: '0%' },
                { y: '105%', duration: 0.1, ease: 'none' }
              );
            });

            const projectLink =
              projectSection[index + 1].querySelector('.js-project-link');
            if (projectLink) {
              projectLink.classList.add('is-active');
            }
          }
        },

        onLeave: () => {
          const projectText =
            projectSection[index].querySelectorAll('.js-project-text');
          projectText.forEach((text) => {
            gsap.fromTo(
              text,
              { y: '0%' },
              { y: '-105%', duration: 0.1, ease: 'none' }
            );
          });

          const projectLink =
            projectSection[index].querySelector('.js-project-link');
          if (projectLink) {
            projectLink.classList.remove('is-active');
          }
        },
      });
    });
  }

  if (banner) {
    const topBanner = document.querySelector('.js-top-banner');
    gsap.to(topBanner, {
      y: '-30%',
      scrollTrigger: {
        trigger: banner,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.5,
        // markers: true
      },
    });
  }

  if (bgColor && project) {
    const projectRect = project.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (projectRect.top + projectRect.height / 2 < windowHeight) {
      bgColor.classList.add('is-black');
    }
  }
}

const details = document.querySelectorAll('.js-details');
const IS_OPENED_CLASS = 'is-opened';

const header = document.getElementById('header');
const nav = document.getElementById('nav');
const navButton = document.getElementById('nav-button');

if (navButton && nav && header) {
  navButton.addEventListener('click', () => {
    header.classList.toggle('is-opened');
    navButton.classList.toggle('is-opened');
    nav.classList.toggle('is-opened');
  });
}

if (nav) {
  const navItems = document.querySelectorAll<HTMLElement>(
    '.js-navigation-item'
  );
  const navImages = document.querySelectorAll<HTMLElement>(
    '.js-navigation-image'
  );

  let currentIndex = 0;

  gsap.set(navImages[currentIndex], { y: 0 });

  navItems.forEach((item, index) => {
    item.addEventListener('mouseenter', () => {
      if (index !== currentIndex) {
        gsap.to(navImages[currentIndex], {
          y: '-100%',
          duration: 0.5,
          ease: 'power2.out',
        });

        gsap.fromTo(
          navImages[index],
          { y: '100%' },
          { y: '0%', duration: 0.5, ease: 'power2.out' }
        );

        currentIndex = index;
      }
    });
  });
}

details.forEach((element) => {
  const summary = element.querySelector('.js-summary');
  const content = element.querySelector('.js-content');

  if (summary) {
    summary.addEventListener('click', (event) => {
      event.preventDefault();

      if (element.classList.contains(IS_OPENED_CLASS)) {
        element.classList.toggle(IS_OPENED_CLASS);

        closingAnim(content, element).restart();
      } else {
        element.classList.toggle(IS_OPENED_CLASS);

        element.setAttribute('open', 'true');

        openingAnim(content).restart();
      }
    });
  }
});

const closingAnim = (content: gsap.TweenTarget, element: Element) =>
  gsap.to(content, {
    height: 0,
    duration: 0.4,
    ease: 'power3.out',
    overwrite: true,
    onComplete: () => {
      element.removeAttribute('open');
    },
  });

const openingAnim = (content: gsap.TweenTarget) =>
  gsap.fromTo(
    content,
    {
      height: 0,
    },
    {
      height: 'auto',
      duration: 0.4,
      ease: 'power3.out',
      overwrite: true,
    }
  );

// fixed viewport 320px
const viewport = document.querySelector('meta[name="viewport"]');
function switchViewport() {
  const value =
    window.outerWidth > 320
      ? 'width=device-width,initial-scale=1'
      : 'width=320';
  if (viewport && viewport.getAttribute('content') !== value) {
    viewport.setAttribute('content', value);
  }
}
addEventListener('resize', switchViewport, false);
switchViewport();

document.addEventListener('DOMContentLoaded', () => {
  const shuffleImages = document.querySelectorAll(
    '.js-shuffle-image'
  ) as NodeListOf<HTMLElement>;
  if (shuffleImages.length !== 0) {
    new ShuffleImages(shuffleImages, 300, 1000);
  }

  const shuffleJplusImages = document.querySelectorAll(
    '.js-shuffle-jplus-image'
  ) as NodeListOf<HTMLElement>;
  if (shuffleJplusImages.length !== 0) {
    new ShuffleImages(shuffleJplusImages, 500, 500);
  }
});

// Parallax
const parallax = document.querySelectorAll('.js-parallax');

if (parallax.length !== 0) {
  parallax.forEach((wrap) => {
    const elements = wrap.querySelectorAll('.js-parallax-item');

    elements.forEach((element) => {
      let y = Number(element.getAttribute('data-y')) || 0;

      if (mql.matches) {
        y = y * 2.5;
      }

      gsap.to(element, {
        y: y,
        scrollTrigger: {
          trigger: wrap,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5,
          // markers: true
        },
      });
    });
  });
}

const stalker = document.querySelector('.js-stalker') as HTMLElement;
const stalkerTargets = document.querySelectorAll('.js-stalker-target');

if (stalker && stalkerTargets.length > 0) {
  stalkerTargets.forEach((target) => {
    const stalkerTextData = (target as HTMLElement).dataset.stalkerText;

    if (stalkerTextData) {
      const stalkerText = document.getElementById(
        'stalker-text'
      ) as HTMLElement;
      stalkerText.textContent = stalkerTextData;
    }

    target.addEventListener('mouseenter', function (event) {
      const mouseEvent = event as MouseEvent;
      stalker.classList.add('is-show');

      const relativeX = mouseEvent.clientX;
      const relativeY = mouseEvent.clientY;

      stalker.style.transform =
        'translate(' + relativeX + 'px, ' + relativeY + 'px)';
    });

    target.addEventListener('mouseleave', function () {
      stalker.classList.remove('is-show');
    });
  });

  document.addEventListener('mousemove', function (event) {
    const mouseEvent = event as MouseEvent;
    if (stalker.classList.contains('is-show')) {
      const relativeX = mouseEvent.clientX;
      const relativeY = mouseEvent.clientY;

      stalker.style.transform =
        'translate(' + relativeX + 'px, ' + relativeY + 'px)';
    }
  });
}

// about move image
const aboutPage = document.getElementById('about-page');
const aboutMoveImageWrapper = document.querySelector(
  '.js-about-move-image-wrapper'
) as HTMLElement;
const aboutMoveImage = document.querySelectorAll('.js-about-move-image');
const aboutSections = document.querySelectorAll('.js-about-section');

if (aboutPage && aboutMoveImageWrapper) {
  aboutPage.addEventListener('mouseenter', function (event) {
    aboutMoveImageWrapper.classList.add('is-show');

    const relativeX = event.clientX;
    const relativeY = event.clientY;

    aboutMoveImageWrapper.style.transform =
      'translate(' + relativeX + 'px, ' + relativeY + 'px)';
  });

  aboutPage.addEventListener('mouseleave', function () {
    aboutMoveImageWrapper.classList.remove('is-show');
  });

  document.addEventListener('mousemove', function (event) {
    if (aboutMoveImageWrapper.classList.contains('is-show')) {
      const relativeX = event.clientX;
      const relativeY = event.clientY;

      aboutMoveImageWrapper.style.transform =
        'translate(' + relativeX + 'px, ' + relativeY + 'px)';
    }
  });
}

if (aboutMoveImage.length !== 0 && aboutSections.length !== 0) {
  aboutSections.forEach((section, index) => {
    let timeoutId: NodeJS.Timeout | null = null;
    section.addEventListener('mouseenter', () => {
      aboutMoveImage[index].classList.add('is-show');

      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    });

    section.addEventListener('mouseleave', () => {
      aboutMoveImage[index].classList.add('is-hide');

      if (timeoutId === null) {
        timeoutId = setTimeout(() => {
          aboutMoveImage[index].classList.remove('is-hide');
          aboutMoveImage[index].classList.remove('is-show');
          timeoutId = null;
        }, 300);
      }
    });
  });
}

// scroll animation trigger
const scrollAnimationTrigger = () => {
  const callback: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        const target = entry.target;
        target.classList.add('is-animated');
      }
    });
  };

  const options = {
    rootMargin: '0px 0px -25%',
  };

  const observer = new IntersectionObserver(callback, options);

  const targets = document.querySelectorAll('[data-animation-trigger]');
  targets.forEach((target) => {
    observer.observe(target);
  });
};
scrollAnimationTrigger();

// switch bg color
const bgSwitchPoints = document.querySelectorAll('.js-bg-switch-point');

if (bgSwitchPoints.length !== 0) {
  bgSwitchPoints.forEach((point) => {
    const pointElement = point as HTMLElement;
    const bgColor = pointElement.dataset.bgColor;

    ScrollTrigger.create({
      trigger: point,
      start: 'top 60%',
      end: 'bottom 60%',
      // markers: true,
      onEnter: () => {
        const targetSectionBg = document.querySelector(
          `.js-section-bg[data-bg-color="${bgColor}"]`
        );
        if (targetSectionBg) {
          targetSectionBg.classList.add('is-active');
        }

        if (bgColor === 'black') {
          document.body.style.setProperty('--text-color', '#fff');
        } else if (bgColor === 'white') {
          document.body.style.setProperty('--text-color', '#000');
        } else {
          document.body.style.setProperty('--text-color', '#fff');
        }

        const aboutPage = document.getElementById('about-page');
        if (aboutPage) {
          const projectPeople = document.getElementById('project-people');
          const sectionBgWhite = document.querySelector('.js-section-bg-white');

          if (sectionBgWhite?.classList.contains('is-active')) {
            projectPeople?.classList.add('is-white');
          } else {
            projectPeople?.classList.remove('is-white');
          }
        }
      },
      onLeaveBack: () => {
        const activeSectionBg = document.querySelector(
          '.js-section-bg.is-active'
        );
        if (activeSectionBg) {
          activeSectionBg.classList.remove('is-active');
        }

        if (bgColor === 'black') {
          document.body.style.setProperty('--text-color', '#000');
        } else if (bgColor === 'white') {
          document.body.style.setProperty('--text-color', '#fff');
        } else {
          document.body.style.setProperty('--text-color', '#000');
        }

        const aboutPage = document.getElementById('about-page');
        if (aboutPage) {
          const projectPeople = document.getElementById('project-people');
          const sectionBgWhite = document.querySelector('.js-section-bg-white');

          if (sectionBgWhite?.classList.contains('is-active')) {
            projectPeople?.classList.add('is-white');
          } else {
            projectPeople?.classList.remove('is-white');
          }
        }
      },
      onEnterBack: () => {
        const activeSectionBg = document.querySelector(
          '.js-section-bg.is-active'
        );
        if (activeSectionBg) {
          activeSectionBg.classList.remove('is-active');
        }

        const targetSectionBg = document.querySelector(
          `.js-section-bg[data-bg-color="${bgColor}"]`
        );
        if (targetSectionBg) {
          targetSectionBg.classList.add('is-active');
        }

        if (bgColor === 'black') {
          document.body.style.setProperty('--text-color', '#fff');
        } else if (bgColor === 'white') {
          document.body.style.setProperty('--text-color', '#000');
        } else {
          document.body.style.setProperty('--text-color', '#fff');
        }

        const aboutPage = document.getElementById('about-page');
        if (aboutPage) {
          const projectPeople = document.getElementById('project-people');
          const sectionBgWhite = document.querySelector('.js-section-bg-white');

          if (sectionBgWhite?.classList.contains('is-active')) {
            projectPeople?.classList.add('is-white');
          } else {
            projectPeople?.classList.remove('is-white');
          }
        }
      },
    });
  });
}

// projects page
const projectsPage = document.getElementById('projects-page');

if (projectsPage) {
  const kv = document.getElementById('kv');
  const fixedBg = document.getElementById('fixed-bg');

  ScrollTrigger.create({
    trigger: kv,
    start: '35% top',
    end: 'bottom bottom',
    // markers: true,
    onEnter: () => {
      if (fixedBg) {
        fixedBg.style.setProperty('--bg-blur', '20px');
      }
    },
    onLeaveBack: () => {
      if (fixedBg) {
        fixedBg.style.setProperty('--bg-blur', '0px');
      }
    },
  });
}

// side scroll
const sideScrollMounts: NodeListOf<Element> =
  document.querySelectorAll('.js-side-scroll');

if (sideScrollMounts.length !== 0 && mqlLg.matches) {
  sideScrollMounts.forEach((mount, index) => {
    const wrapper = document.querySelectorAll('.js-side-scroll-wrapper')[index];
    const content = mount.querySelector(
      '.js-side-scroll-content'
    ) as HTMLElement;

    gsap.to(content, {
      x: () => -(content.clientWidth - wrapper.clientWidth),
      ease: 'none',
      scrollTrigger: {
        trigger: mount,
        start: 'top top',
        end: () => `+=${content.clientWidth - wrapper.clientWidth}`,
        scrub: true,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });
  });
}

const recruitment = document.getElementById('recruitment');
if (recruitment) {
  const topBanner = document.querySelector('.js-fixed-bg');
  gsap.to(topBanner, {
    y: '-30%',
    scrollTrigger: {
      trigger: recruitment,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.5,
      // markers: true
    },
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const thumbnailContainers = document.querySelectorAll<HTMLElement>(
    '.js-thumbnail-image'
  );

  if (thumbnailContainers.length !== 0) {
    thumbnailContainers.forEach((container) => {
      const thumbnails = container.querySelectorAll<HTMLImageElement>('img');
      if (thumbnails.length === 0) return;

      let currentIndex = 0;

      thumbnails.forEach((thumbnail, index) => {
        if (index !== 0) thumbnail.style.opacity = '0';
      });

      setInterval(() => {
        thumbnails[currentIndex].style.opacity = '0';

        currentIndex = (currentIndex + 1) % thumbnails.length;

        thumbnails[currentIndex].style.opacity = '1';
      }, 250);
    });
  }
});

const sideNavItems = document.querySelectorAll('.js-project-nav-item');

if (sideNavItems.length > 0) {
  console.log('sideNavItems', sideNavItems);
  const jobSections = document.querySelectorAll('.js-project-section');

  const observerOptions = {
    root: null,
    rootMargin: '-40% 0px -90% 0px',
    threshold: 0,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const targetId = entry.target.getAttribute('id');
      const navItem = document.querySelector(
        `.js-project-nav-item[href="#${targetId}"]`
      );

      if (entry.isIntersecting) {
        sideNavItems.forEach((item) => {
          item.classList.remove('is-active');
        });

        if (navItem) {
          navItem.classList.add('is-active');
        }
      }
    });
  }, observerOptions);

  jobSections.forEach((section) => {
    observer.observe(section);
  });
}

// pickup magazine swiper
const pickUpMagazineSwiper = document.querySelector(
  '.js-pickup-magazine-swiper'
) as HTMLElement;

if (pickUpMagazineSwiper) {
  const swiper = new Swiper(pickUpMagazineSwiper, {
    modules: [Autoplay, Pagination, EffectFade],
    init: false,
    loop: true,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    speed: 300,

    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },

    pagination: {
      el: '.js-magazine-pickup-pagination',
      clickable: true,
      renderBullet: function (index, className) {
        return `<span class="${className}" data-num="${index}"><span class="num">${index + 1}</span><svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33"><circle cx="16" cy="16.5" r="15.5" stroke="black"/></svg></span>`;
      },
    },
  });

  swiper.init();
}

// other magazine swiper
const otherMagazineSwiper = document.querySelector('.js-other-magazine-swiper');

if (otherMagazineSwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (mql.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper('.js-other-magazine-swiper', {
      modules: [Navigation],
      init: false,
      loop: false,
      slidesPerView: 2.5,
      spaceBetween: 40,

      navigation: {
        nextEl: '.js-other-magazine-next',
        prevEl: '.js-other-magazine-prev',
      },

    });

    swiper.init();
  };

  mql.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}
